export interface PointDefinition {
    readonly x: number;
    readonly y: number;
}

export interface SegmentDefinition {
    readonly A: PointDefinition;
    readonly B: PointDefinition;
}

export class Segment implements SegmentDefinition {
    constructor(public readonly A: PointDefinition, public readonly B: PointDefinition) {}

    private _quadLength: number;
    get quadLength() {
        if (!this._quadLength) {
            this._quadLength = GeometryHelper.quadraticDistanceBetweenPoints(this.A, this.B);
        }
        return this._quadLength;
    }

    private _length: number;
    get length() {
        if (!this._length) {
            this._length = Math.sqrt(this._quadLength);
        }
        return this._length;
    }

    private _slope: number;
    get slope() {
        if (!this._slope) {
            this._slope = GeometryHelper.slope(this.A, this.B);
        }
        return this._slope;
    }

    private _slopePerpendicular: number;
    get slopePerpendicular() {
        if (!this._slopePerpendicular) {
            this._slopePerpendicular = GeometryHelper.slopePerpendicular(this.A, this.B);
        }
        return this._slopePerpendicular;
    }

    private _centerPoint: PointDefinition;
    get centerPoint(): PointDefinition {
        if (!this._centerPoint) {
            this._centerPoint = GeometryHelper.getPointInSegment(this.A, this.B, 0.5);
            // this._centerPoint = {
            //     x: (this.A.x + this.B.x)/2,
            //     y: (this.A.y + this.B.y)/2
            // };
        }
        return this._centerPoint;
    }
}

export interface CircleDefinition {
    readonly center: PointDefinition;
    readonly radius: number;
}
export class Circle implements CircleDefinition {
    constructor(public readonly center: PointDefinition, public readonly radius: number) {}
    translate(dx: number, dy: number) {
        let centerPoint = {
            x: this.center.x + dx,
            y: this.center.y + dy
        };
        return new Circle(centerPoint, this.radius);
    }
    scale(ratio: number, freeTerm: number = 0) {
        let newRadius: number = this.radius * ratio + freeTerm;
        return new Circle(this.center, Math.max(0, newRadius));
    }
    scaleWith(amount: number) {
        return this.scale(1, amount);
    }
    scaleBy(amount: number) {
        return this.scale(amount);
    }
}

export class GeometryHelper {
    static slope(A: PointDefinition, B: PointDefinition) {
        if (A.x === B.x) {
            // the line is vertical, the slope is infinite
            return Infinity;
        }
        if (A.y === B.y) {
            // the line is horizontal, the slope is 0.
            // I'm returning 0 because the formula might return -0 and while this may be valid JS is not valid math
            return 0;
        }
        return (B.y - A.y)/(B.x - A.x);
    }
    static slopePerpendicular(A: PointDefinition, B: PointDefinition) {
        if (A.y === B.y) {
            // the line is horizontal, the slope is 0, so the perpendicular line will have the slope Infinity
            return Infinity;
        }
        if (A.x === B.x) {
            // the line is vertical, the slope is infinite, so the perpendicular line will have the slope 0
            // I'm returning 0 because the formula might return -0 and while this may be valid JS is not valid math
            return 0;
        }
        return (A.x - B.x)/(B.y - A.y);
    }

    /**
     * normalize == true means that we force the radians to be between [0, 2*Math.PI)
     * If you don't want normalization call the function with the second parameter "false"
     *
     * Observations:
     * For normalization between [0, 2*Math.PI) use Math.floor at the factor computation
     * For normalization between [-Math.PI, Math.PI) use Math.round at the factor computation
     */
    static degreesToRadians(degrees: number, normalize: boolean = true) {
        if (normalize) {
            let factor = Math.floor(degrees / 360);
            degrees = degrees - factor * 360;
        }
        return degrees * (Math.PI / 180);
    }

    /**
     * normalize == true means that we force the radians to be between [0, 360)
     * If you don't want normalization call the function with the second parameter "false"
     *
     * Observation.
     * For normalization between [0, 360) use Math.floor at the factor computation
     * For normalization between [-180, 180) use Math.round at the factor computation
     */
    static radiansToDegrees(radians: number, normalize: boolean = true) {
        let degrees = radians * (180 / Math.PI);
        if (!normalize) {
            return degrees;
        }
        let factor = Math.floor(degrees / 360);
        return degrees - factor * 360;
    }

    /**
     *
     */
    static slopeToAngle(slope: number, resultInDegrees: boolean = true) {
        let radians = Math.atan(slope);
        return resultInDegrees ? GeometryHelper.radiansToDegrees(radians) : radians;
    }

    static quadraticDistanceBetweenPoints(A: PointDefinition, B: PointDefinition) {
        return Math.pow(A.x - B.x, 2) + Math.pow(A.y - B.y, 2);
    }

    static distanceBetweenPoints(A: PointDefinition, B: PointDefinition) {
        return Math.sqrt(GeometryHelper.quadraticDistanceBetweenPoints(A, B));
    }

    /**
     * In a segment OA of lenght d, get the point D that is between O and A, at a distance of d1 from O and d - d1 from A
     */
    static getIntermediateCoordinate(x1: number, x2: number, ratio: number): number {
        return x1 + ((x2 - x1)*ratio);
    }
    /**
     * In a segment OA of lenght d, get the point D that is between O and A, at a distance of d1 from O and d - d1 from A
     */
    static getPointInSegment(O: PointDefinition, A: PointDefinition, ratio: number): PointDefinition {
        let x = GeometryHelper.getIntermediateCoordinate(O.x, A.x, ratio);
        let y = GeometryHelper.getIntermediateCoordinate(O.y, A.y, ratio);
        return {x, y};
    }

    static getCenterOfDOMRect(r: DOMRect): PointDefinition {
        // same as getIntermediateCoordinate or getPointInSegment with ratio 0.5 (1/2) but it does 2 mathematic operations instead of 4
        // the equivalent is getIntermediateCoordinate(r.left, r.left + r.width, 0.5)
        // probably an unnecessary optimization but anyway
        return {
            x: r.left + r.width/2,
            y: r.top + r.height/2
        };
    }

    /**
     * In romanian is "cercul circumscris dreptunghiului" I hope google translate did a good job here
     */
    static getCircleCircumscribedToTheRectangle(r: DOMRect): Circle {
        let center = GeometryHelper.getCenterOfDOMRect(r);
        return new Circle(center, GeometryHelper.distanceBetweenPoints(center, {x: r.left, y: r.top}));
    }
}

if (!window["GeometryHelper"]) {
    window["GeometryHelper"] = GeometryHelper;
}
